import { router } from 'expo-router';

import { isSigningOut, signOut } from '@/services/auth';
import { store, userIdAtom } from '@/services/globalState';
import { isPendingOAuthUserId } from '@/util/authTokens';
import { Sentry } from '@/util/sentry';

const HTTP_STATUS_FOR_USER_LOGOUT = [401];

/**
 * Global logout handler for HTTP responses
 *
 * Centralized guard that forces sign-out only when session is invalid.
 * Protects against accidental logouts from role-gated endpoints.
 *
 * Status code handling:
 * - 401: Forces logout with alert (invalid/expired session)
 * - 404 on /v2/users/me: Forces logout (user has JWT but no DB record)
 * - 403: Does NOT logout (auth'd but not authorized - handle locally)
 * - 5xx: Not handled (surface to caller for normal error handling)
 *
 * Does not auto-logout pending OAuth users (Cognito UUID userId).
 *
 * Skips execution if manual logout already in progress to prevent double-logout.
 */
export async function logoutSelectStatusCode(statusCode: number, url?: string) {
  if (isSigningOut()) {
    return;
  }

  const currentUserId = store.get(userIdAtom);

  if (!currentUserId || isPendingOAuthUserId(currentUserId)) {
    return;
  }

  const isUserNotFound = statusCode === 404 && url?.endsWith('/v2/users/me');

  const shouldLogout = HTTP_STATUS_FOR_USER_LOGOUT.includes(statusCode) || isUserNotFound;

  if (shouldLogout) {
    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'Log out user after receiving HTTP status code',
      level: 'warning',
      data: { statusCode, isUserNotFound, userId: currentUserId },
    });

    if (isUserNotFound) {
      Sentry.captureMessage('User has valid JWT but no database record', {
        level: 'warning',
        tags: { reason: 'user_not_found_404' },
        extra: { userId: currentUserId, url },
      });
    }

    try {
      await signOut(
        () => {
          router.replace('/');
        },
        isUserNotFound ? 'user_not_found' : undefined
      );
    } catch (e) {
      console.error('[Auth] Auto-logout failed:', e);

      Sentry.captureException(e);
    }
  }
}
